import { ReactNode } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

interface EnhancedModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  title: string;
  description?: string;
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "7xl";
  onClose?: () => void;
  preventOutsideClick?: boolean;
  className?: string;
  contentClassName?: string;
}

const maxWidthClasses = {
  sm: "max-w-sm",
  md: "max-w-md", 
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl"
};

export function EnhancedModal({
  open,
  onOpenChange,
  children,
  title,
  description,
  maxWidth = "4xl",
  onClose,
  preventOutsideClick = true,
  className = "",
  contentClassName = ""
}: EnhancedModalProps) {
  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      onOpenChange(false);
    }
  };

  const modalId = `enhanced-modal-${title.toLowerCase().replace(/\s+/g, '-')}`;
  const wrapperClass = `enhanced-modal-wrapper-${title.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <>
      {/* Global Styles for Modal */}
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        
        /* Additional scrollbar hiding for all browsers */
        .${wrapperClass} *::-webkit-scrollbar {
          display: none;
        }
        .${wrapperClass} * {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        /* Specifically target all scrollable areas in the modal */
        .${wrapperClass} .overflow-y-auto,
        .${wrapperClass} .overflow-x-auto,
        .${wrapperClass} .overflow-auto {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .${wrapperClass} .overflow-y-auto::-webkit-scrollbar,
        .${wrapperClass} .overflow-x-auto::-webkit-scrollbar,
        .${wrapperClass} .overflow-auto::-webkit-scrollbar {
          display: none;
        }
        
        button[data-radix-dialog-close] {
          display: none !important;
        }
        .${wrapperClass} button[aria-label="Close"] {
          display: none !important;
        }
        .${wrapperClass} [data-radix-dialog-close] {
          display: none !important;
        }
        .${wrapperClass} .main-content {
          z-index: 1 !important;
          position: relative;
        }
        
        /* Prevent auto-scroll for form elements */
        .${wrapperClass} * {
          scroll-margin-top: 0 !important;
          scroll-margin-bottom: 0 !important;
          scroll-padding: 0 !important;
        }
        
        /* Override any scroll-into-view behavior */
        .${wrapperClass} input:focus,
        .${wrapperClass} button:focus {
          scroll-margin: 0 !important;
          scroll-behavior: auto !important;
        }
        
        /* Fix aria-hidden focus conflicts */
        [data-state="closed"][aria-hidden="true"] * {
          pointer-events: none !important;
        }
        
        [data-state="closed"][aria-hidden="true"] input,
        [data-state="closed"][aria-hidden="true"] button {
          visibility: hidden !important;
        }

        /* Force z-index and pointer events for Select components */
        [data-radix-select-content],
        [data-radix-select-viewport],
        [data-radix-select-item],
        [data-radix-portal] {
          z-index: 200 !important;
          pointer-events: auto !important;
        }

        /* Prevent scroll on Select trigger focus */
        .dialog-content-wrapper [data-radix-select-trigger],
        .dialog-content-wrapper [data-radix-select-trigger]:focus,
        .dialog-content-wrapper [data-radix-select-trigger]:focus-visible {
          scroll-margin: 0 !important;
          scroll-padding: 0 !important;
          scroll-behavior: auto !important;
        }
      `}</style>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent 
          className={`${wrapperClass} dialog-content-wrapper w-[95vw] ${maxWidthClasses[maxWidth]} h-[95vh] max-h-[95vh] p-0 border-0 shadow-2xl backdrop-blur-sm rounded-2xl overflow-hidden ${className}`}
          onInteractOutside={(e) => {
            // Handle preventOutsideClick prop
            if (preventOutsideClick) {
              e.preventDefault();
              return;
            }
            
            // Allow clicks on Radix Select components (which render in portals)
            const target = e.target as HTMLElement;
            if (
              target.closest('[data-radix-select-content]') ||
              target.closest('[data-radix-select-viewport]') ||
              target.closest('[data-radix-select-item]') ||
              target.closest('[data-radix-portal]')
            ) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            handleClose();
          }}
          aria-labelledby={`${modalId}-title`}
          aria-describedby={`${modalId}-description`}
          role="dialog"
          aria-modal="true"
        >
          {/* Required accessibility components - visually hidden */}
          <DialogTitle asChild>
            <VisuallyHidden>
              <h2 id={`${modalId}-title`}>{title}</h2>
            </VisuallyHidden>
          </DialogTitle>
          {description && (
            <DialogDescription asChild>
              <VisuallyHidden>
                <p id={`${modalId}-description`}>{description}</p>
              </VisuallyHidden>
            </DialogDescription>
          )}

          {/* Custom Close Button - iOS Safe Area Support */}
          <button
            onClick={handleClose}
            className="absolute right-3 z-50 w-9 h-9 bg-white/90 rounded-full shadow-lg hover:shadow-xl flex items-center justify-center text-gray-500 hover:text-gray-700 transition-all duration-200 border border-gray-200/50 backdrop-blur-sm hover:bg-white"
            style={{ top: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
            type="button"
            aria-label="Close modal"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Enhanced Background Effects */}
          <div className="absolute inset-0 rounded-2xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-50/20 via-transparent to-slate-50/30"></div>
            <div className="absolute top-0 left-0 w-72 h-72 bg-gradient-to-br from-orange-400/10 to-orange-600/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-48 h-48 bg-gradient-to-br from-orange-400/8 to-pink-400/6 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
          </div>
          
          {/* Main Content Container */}
          <div className="h-full relative main-content">
            <div className="absolute inset-0 overflow-y-auto scrollbar-hide">
              <div className="min-h-full">
                <div className={`p-6 sm:p-8 ${contentClassName}`}>
                  {children}
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
